/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useRef, useState } from 'react';
import { PropTypes } from 'prop-types';
import styled, { css } from 'styled-components';

import { FontWeight, Text, FiraSansExtraLight, FiraSansBold } from '../Text';
import Icon from '../Icon';
import Colors from 'constants/Colors';
import Label from './Label';
import ValidationError from './ValidationError';
import { isEqualObject } from 'utils/src/object-util';
import { Events } from 'constants/Events';
import { Breakpoints } from "../../constants/Breakpoints";
import { mediaHelper } from "../../utils/src";
import _ from 'lodash';

const propTypes = {
  placeholder: PropTypes.string,
  format: PropTypes.func,
  label: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.any).isRequired,
  tabIndex: PropTypes.number,
  isRequired: PropTypes.bool,
  optionMapper: PropTypes.func,
  onChange: PropTypes.func,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  validation: PropTypes.func,
};

const defaultProps = {
  placeholder: '',
  format: selectedSize => `${selectedSize}`,
  label: null,
  isRequired: false,
  tabIndex: 0,
  optionMapper: opt => opt,
  onChange: value => value,
  onOpen: value => value,
  onClose: value => value,
  validation: true,
};

const SelectContainer = styled.div`
  position: relative;
  user-select: none;
  outline: none;
  margin-bottom: 10px;
  text-align: ${({ alignCenter }) => (alignCenter ? 'center' : 'initial')};
  ${FiraSansExtraLight}
  
  height: 24px;
    
  @media screen and (min-width: ${Breakpoints.sm}) {
    height: 42px;
  }
    
  @media screen and (min-width: ${Breakpoints.md}) {
    margin-bottom: 0;
  }
`;

const SelectedOptionContainer = styled.div`
  height: inherit;
  border: 1px solid ${Colors.GRAY[3]};
  background-color: ${Colors.WHITE};
  border-radius: 4px;
  display: flex;
  align-items: center;
  cursor: pointer;
  text-align: ${({ alignCenter }) => (alignCenter ? 'center' : 'initial')};
  ${FiraSansBold}

  &:focus {
    border-color: ${Colors.SECONDARY};
  }

  ${({ hasValue }) =>
    hasValue &&
    css`
      
      background-color-old: ${Colors.GRAY[3]};
      background-color: ${Colors.WHITE};
    `}

  ${({ isOpen }) =>
    isOpen &&
    css`
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      border-color: ${Colors.SECONDARY};
    `}

  ${({ error, isOpen, touched }) =>
    error &&
    !isOpen &&
    touched &&
    css`
      border: 3px solid ${Colors.DANGER};

      & ${FontIcon} {
        color: ${Colors.DANGER};
      }
    `}
`;

const PlaceholderContainer = styled.div`
  margin-left: 4px;
  font-size: 14px;
  line-height: normal;
  flex: 1;
  color: ${Colors.GRAY[0]};
  min-width: 0;
  text-align: ${({ alignCenter }) => (alignCenter ? 'center' : 'initial')};
  ${FiraSansBold}

  color: ${({ corDinamica }) => (corDinamica ? Colors.GRAY[6] : Colors.GRAY[3])};
  
  ${({ hasValue, disabledActive }) =>
    (hasValue || disabledActive) &&
    css`
      color: ${Colors.GRAY[1]};
    `}

  & ${Text} {
    text-transform: capitalize;
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const FontIcon = styled(({ isOpen, ...props }) => <Icon {...props} />)`
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${Colors.GRAY[0]};
  transition: transform 0.3s ease;

  ${({ isOpen }) =>
    isOpen &&
    css`
      color: ${Colors.SECONDARY};
      transform: rotate(180deg);
    `}
    
  margin: 1px;
  
  @media screen and (min-width: ${Breakpoints.sm}) {
    margin: 10px 6px 10px 10px;
  }
`;

export const OptionsContainer = styled.ul`
  position: absolute;
  z-index: 10;
  left: 0;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 300px;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  border-left: 1px solid ${Colors.SECONDARY};
  border-right: 1px solid ${Colors.SECONDARY};
  border-bottom: 1px solid ${Colors.SECONDARY};
  background-color: #fff;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  top: 23px;
  
  ${mediaHelper.sm`
    top: 30px;
  `}
`;

export const Option = styled.li`
  list-style: none;
  min-height: 42px;
  display: flex;
  align-items: center;
  padding: 10px;
  font-size: 14px;
  color: ${Colors.GRAY[0]};
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid ${Colors.GRAY[3]};

  ${({ selected }) =>
    selected &&
    css`
      background-color: ${Colors.Alpha(Colors.SECONDARY, 0.7)};
    `}

  &:hover {
    background-color: ${Colors.GRAY[4]};
  }
`;

const CheckBoxIcon = styled(Icon)`
  font-size: 14px;
  margin-right: 8px;

  ${({ checked }) =>
    checked &&
    `
    color: ${Colors.GRAY[0]};
  `}
`;

const MultiSelectField = ({
  placeholder,
  format,
  label,
  options,
  tabIndex,
  isRequired,
  optionMapper,
  isEqual,
  validationMinHeight,
  validation,
  field,
  form: { setFieldValue, setFieldTouched, touched, errors },
  onChange,
  onClose,
  onOpen,
  chips,
  maxLength,
  ...props
}) => {
  const isClickOrTouchEvent = useRef(false);
  const [isOpen, setIsOpen] = useState(false);

  const meta = {
    isOpen,
    touched: touched[field.name],
    error: errors[field.name]
  };

  const _changeOpen = openState => {
    setIsOpen(openState);
    if (openState) {
      onOpen();
    } else {
      onClose();
    }
  };

  const _toggleField = () => {
    _changeOpen(!meta.isOpen);
  };

  return (
    <div {...props}>
      {label && (
        <Label meta={meta} isRequired={isRequired} weight={FontWeight.SemiBold}>
          {label}
        </Label>
      )}
      <SelectContainer
        data-name={field.name}
        onMouseDown={e => {
          isClickOrTouchEvent.current = e.button === Events.MOUSE_KEY_DOWN_CODE;
        }}
        onTouchStart={() => {
          isClickOrTouchEvent.current = true;
        }}
        onClick={_toggleField}
        tabIndex={tabIndex}
        onFocus={() => {
          _changeOpen(!isClickOrTouchEvent.current);
          isClickOrTouchEvent.current = false;
        }}
        onBlur={() => {
          setFieldTouched(field.name, true, true);
          _changeOpen(false);
          onClose();
        }}
        onKeyDown={e => {
          if (e.key.toLowerCase() === 'enter') {
            _toggleField();
          }
        }}
      >
        <SelectedOptionContainer {...meta}>
          <PlaceholderContainer hasValue={field.value}>
            <Text weight={FontWeight.SemiBold} style={{ maxWidth: 200 }}>
              {field.value ? format(field.value) : placeholder}
            </Text>
          </PlaceholderContainer>
          <FontIcon name="arrow-down" isOpen={meta.isOpen} />
        </SelectedOptionContainer>
        {meta.isOpen && (
          <OptionsContainer>
            {options.map((opt, index) => {

              const fieldValue = field.value || [];
              const isSelected = fieldValue.some(value => opt.value === value || _.isEqual(value[0], opt.value))

              return (
                <Option
                  key={`opt-${field.name}-${index}`}
                  onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();

                    let selectedValues = [...fieldValue];
                    const alreadySelected = selectedValues.some(value => opt.value === value)

                    if (alreadySelected) {
                      selectedValues = selectedValues.filter(value => value !== opt.value);
                    } else {
                      if (maxLength && selectedValues.length === maxLength) return;
                      selectedValues.push(opt.value);
                    }

                    setFieldValue(field.name, selectedValues);
                    onChange(selectedValues);
                  }}
                  selected={isSelected}
                >
                  {isSelected
                    ? <CheckBoxIcon name="check-checked" checked />
                    : <CheckBoxIcon name="check" />
                  }

                  <Text weight={isSelected ? FontWeight.Bold : null}>
                    {opt.label}
                  </Text>
                </Option>
              );
            })}
          </OptionsContainer>
        )}
      </SelectContainer>
      {validation && (<ValidationError meta={meta} validationMinHeight={validationMinHeight} />)}

    </div>
  );
};

MultiSelectField.propTypes = propTypes;
MultiSelectField.defaultProps = defaultProps;

export default MultiSelectField;
